// Chess board — 8×8 grid, brown/cream squares, file/rank corner
// labels, piece art, click-to-move with legal-target highlights,
// last-move gold tint, in-check red tint, and board-orientation /
// piece-rotation handling per player spec.
//
// Square-colour rule: a1 is DARK → `(fileIdx + rank) % 2 === 0`
// means LIGHT, regardless of which side the viewer is playing.
//
// Click behaviour (mandated by user spec):
//   1. Click own piece → select it, dot-highlight legal targets.
//   2. Click a dot-highlighted target → dispatch move (via onMove).
//   3. Click another own piece → switch selection.
//   4. Click opponent's piece / empty square with no selection →
//      NOTHING (we never preview opponent legal moves).
//
// Orientation handling:
//   • `orientation='white'` — files a..h left→right, ranks 8..1 top→bot
//   • `orientation='black'` — files h..a left→right, ranks 1..8 top→bot
//     AND each piece image is rotated 180° so black's pieces stand
//     upright from black's POV (user spec). White's pieces, now at
//     the top of black's screen, appear with their "back" to black
//     — same physical reading as playing over a real board.

import { useMemo } from 'react';
import { parseFen, legalMovesFrom, squareToIdx, idxToSquare } from './rules.js';

export type PieceCode =
  | 'wK' | 'wQ' | 'wR' | 'wB' | 'wN' | 'wP'
  | 'bK' | 'bQ' | 'bR' | 'bB' | 'bN' | 'bP';

type Square = string;   // 'a1' .. 'h8'

type Props = {
  /** FEN string describing the current position. Rendered as-is. */
  fen: string;
  /** Which side the viewer is playing as. Flips board + rotates
   *  piece art 180° when `'black'`. */
  orientation: 'white' | 'black';
  /** Which side is currently on-move. Click-to-move only fires when
   *  this matches the viewer's own side. */
  turn: 'w' | 'b';
  /** Viewer's own side, used to gate interaction (spectators can't
   *  move). Null during the brief VS-intro window. */
  mySide: 'w' | 'b' | null;
  /** Last move played — both squares get the gold tint. */
  lastMove?: { from: Square; to: Square } | null;
  /** Square of a king currently in check (red tint + shadow). */
  checkSq?: Square | null;
  /** Currently-selected square (the piece the viewer has clicked). */
  selectedSq?: Square | null;
  /** Pre-computed legal target squares for `selectedSq`. Dot for
   *  empty targets, ring for capture targets. */
  legalTargets?: Square[];
  /** Called when the viewer clicks any square. Parent decides whether
   *  to select, move, or ignore based on the square's contents. */
  onSquareClick: (sq: Square) => void;
  /** `true` when the match is finished — disables all clicks. */
  disabled?: boolean;
};

export default function Board({
  fen, orientation, turn, mySide,
  lastMove, checkSq, selectedSq, legalTargets = [],
  onSquareClick, disabled = false,
}: Props) {
  // Parse FEN once per render; cheap enough (64 cells) that memoising
  // against `fen` is enough. Failing parse means the server sent us
  // garbage — render an empty board so the UI doesn't crash.
  const pieces = useMemo(() => {
    try {
      const pos = parseFen(fen);
      const out: Record<Square, PieceCode> = {};
      for (let i = 0; i < 64; i++) {
        if (pos.board[i]) out[idxToSquare(i)] = pos.board[i] as PieceCode;
      }
      return out;
    } catch {
      return {};
    }
  }, [fen]);

  const filesLTR = orientation === 'white'
    ? ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h']
    : ['h', 'g', 'f', 'e', 'd', 'c', 'b', 'a'];
  const ranksTTB = orientation === 'white'
    ? [8, 7, 6, 5, 4, 3, 2, 1]
    : [1, 2, 3, 4, 5, 6, 7, 8];
  const labelRank = ranksTTB[ranksTTB.length - 1];
  const labelFile = filesLTR[filesLTR.length - 1];

  const legalSet = new Set(legalTargets);
  const canInteract = !disabled && mySide && turn === mySide;

  return (
    <div
      className={`chess-board ${orientation === 'black' ? 'chess-board--flipped' : ''}`}
      role="grid"
      aria-label="Chess board"
      data-my-side={mySide || ''}
    >
      {ranksTTB.map((rank) =>
        filesLTR.map((file) => {
          const fileIdx = file.charCodeAt(0) - 'a'.charCodeAt(0);
          const isLight = (fileIdx + rank) % 2 === 0;
          const square: Square = `${file}${rank}`;
          const piece = pieces[square];

          const isSelected   = selectedSq === square;
          const isLegalMove  = legalSet.has(square);
          const isLastFrom   = lastMove?.from === square;
          const isLastTo     = lastMove?.to === square;
          const isCheck      = checkSq === square;
          const isCapTarget  = isLegalMove && Boolean(piece);

          const cls = [
            'chess-square',
            isLight ? 'chess-square--light' : 'chess-square--dark',
            (isLastFrom || isLastTo) ? 'chess-square--last' : '',
            isCheck ? 'chess-square--check' : '',
            isSelected ? 'chess-square--selected' : '',
          ].filter(Boolean).join(' ');

          return (
            <div
              key={square}
              data-square={square}
              className={cls}
              role="gridcell"
              onClick={canInteract ? () => onSquareClick(square) : undefined}
              style={canInteract ? { cursor: 'pointer' } : undefined}
            >
              {rank === labelRank && (
                <span className="chess-file-label" aria-hidden>{file}</span>
              )}
              {file === labelFile && (
                <span className="chess-rank-label" aria-hidden>{rank}</span>
              )}
              {piece && (
                <img
                  src={`/games/chess/pieces/${piece}.png`}
                  alt={piece}
                  className="chess-piece"
                  draggable={false}
                />
              )}
              {/* Legal-target affordance. Empty square → small dot.
                  Capture target → a ring around the enemy piece. */}
              {isLegalMove && !isCapTarget && (
                <span className="chess-legal-dot" aria-hidden />
              )}
              {isLegalMove && isCapTarget && (
                <span className="chess-legal-ring" aria-hidden />
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

/** Helper so Page.tsx can compute legal targets without importing
 *  rules.js directly. Returns the list of destination squares the
 *  given piece can legally move to from `fromSq`. */
export function legalTargetsFrom(fen: string, fromSq: Square): Square[] {
  try {
    const pos = parseFen(fen);
    const idx = squareToIdx(fromSq);
    const moves = legalMovesFrom(pos, idx);
    // Dedupe: promotion moves emit one move per promotion piece; the
    // UI only cares about the target square.
    const set = new Set<string>();
    for (const m of moves) set.add(idxToSquare(m.to));
    return Array.from(set);
  } catch {
    return [];
  }
}

/** Helper so Page.tsx can detect if a move requires promotion without
 *  replicating rules logic. Returns `true` if ANY legal move from `from`
 *  to `to` carries a `promotion` flag. */
export function isPromotionMove(fen: string, from: Square, to: Square): boolean {
  try {
    const pos = parseFen(fen);
    const moves = legalMovesFrom(pos, squareToIdx(from));
    return moves.some((m) => idxToSquare(m.to) === to && Boolean(m.promotion));
  } catch {
    return false;
  }
}
